/**
 * Routing for an app that is usually not in a browser.
 *
 * Three things make this different from a website router:
 *
 * 1. **No hash.** A Home Screen web app has no address bar, so a `#/board` URL buys
 *    nothing and loses the ability to share a real link. Routes are real paths inside the
 *    app's scope, pushed with the History API; the service worker serves the shell for any
 *    path under that scope, which is what makes a deep link survive a cold launch.
 * 2. **Back is not guaranteed to exist.** In standalone display mode there is no browser
 *    chrome and, on iOS, no reliable system back gesture either. `canGoBack` reports
 *    whether history actually has somewhere to go *inside this app*, and every screen draws
 *    its own back affordance rather than assuming one is provided.
 * 3. **Deep links land before the app is ready.** `/p/<id>` and `/pack/<id>` are parsed at
 *    module load, before any project has been opened, so `main.ts` can restore the right
 *    document instead of opening the last one and then jumping.
 */

import { signal, type ReadonlySignal } from '../core/signal';
import { createLogger, idPrefix, tag } from '../core/log';
import { prefersReducedMotion } from '../ui/components/controls';
import { isStandalone } from '../platform/standalone';

const log = createLogger('router');

export type RouteName =
  | 'home'
  | 'board'
  | 'library'
  | 'wave'
  | 'sound-lab'
  | 'voice-lab'
  | 'sequencer'
  | 'mixer'
  | 'export'
  | 'settings'
  | 'onboarding'
  | 'pack';

export interface Route {
  readonly name: RouteName;
  /** Project id for `/p/<id>/…`, pack id for `/pack/<id>`, asset id for the wave editor. */
  readonly projectID?: string;
  readonly packID?: string;
  readonly assetID?: string;
  readonly padID?: string;
}

export interface RouteDefinition {
  readonly name: RouteName;
  /** Shown in the tab rail; screens not listed are reached from inside another screen. */
  readonly tab?: { title: string; symbol: string; order: number };
  /** Where the back affordance goes when history is empty — never a dead end. */
  readonly fallbackBack?: RouteName;
  readonly showsTransport: boolean;
  readonly showsTabs: boolean;
  readonly title: string;
}

export const ROUTES: Readonly<Record<RouteName, RouteDefinition>> = Object.freeze({
  home: { name: 'home', tab: { title: 'Home', symbol: 'folder.fill', order: 0 }, showsTransport: false, showsTabs: true, title: 'Projects' },
  board: { name: 'board', tab: { title: 'Board', symbol: 'square.grid.3x3.fill', order: 1 }, showsTransport: true, showsTabs: true, title: 'Board' },
  sequencer: { name: 'sequencer', tab: { title: 'Beats', symbol: 'music.note.list', order: 2 }, showsTransport: true, showsTabs: true, title: 'Sequencer' },
  'voice-lab': { name: 'voice-lab', tab: { title: 'Voice', symbol: 'mouth.fill', order: 3 }, showsTransport: false, showsTabs: true, title: 'Voice Lab' },
  library: { name: 'library', tab: { title: 'Library', symbol: 'waveform.path', order: 4 }, showsTransport: false, showsTabs: true, title: 'Library' },
  'sound-lab': { name: 'sound-lab', fallbackBack: 'board', showsTransport: true, showsTabs: false, title: 'Sound Lab' },
  wave: { name: 'wave', fallbackBack: 'library', showsTransport: false, showsTabs: false, title: 'Sample Editor' },
  mixer: { name: 'mixer', fallbackBack: 'board', showsTransport: true, showsTabs: false, title: 'Mixer' },
  export: { name: 'export', fallbackBack: 'board', showsTransport: false, showsTabs: false, title: 'Export' },
  settings: { name: 'settings', fallbackBack: 'home', showsTransport: false, showsTabs: false, title: 'Settings' },
  onboarding: { name: 'onboarding', showsTransport: false, showsTabs: false, title: 'Welcome' },
  pack: { name: 'pack', fallbackBack: 'library', showsTransport: false, showsTabs: false, title: 'Sound Pack' },
});

export const TAB_ROUTES: readonly RouteDefinition[] = Object.values(ROUTES)
  .filter((entry): entry is RouteDefinition & { tab: NonNullable<RouteDefinition['tab']> } => entry.tab !== undefined)
  .sort((a, b) => a.tab.order - b.tab.order);

/**
 * The app's own root path.
 *
 * `import.meta.url` is the only value that is right in both builds: in development this
 * module is served from `<scope>/src/app/router.ts` and in production from
 * `<scope>/assets/index-<hash>.js`, so walking up to the scope needs a different number of
 * segments in each. `BASE_URL` carries it in production; the dev server always serves from
 * the server root.
 */
function readScope(): string {
  const base = import.meta.env.BASE_URL;
  if (typeof base === 'string' && base.startsWith('/')) return base.endsWith('/') ? base : `${base}/`;
  // `base: './'` in the Vite config produces a relative BASE_URL, which cannot be resolved
  // without a document. Fall back to the directory the page itself was served from.
  const path = location.pathname;
  const marker = path.indexOf('/index.html');
  if (marker >= 0) return path.slice(0, marker + 1);
  for (const segment of ['/p-', '/pack-', '/edit-', '/settings', '/library', '/board', '/welcome']) {
    const at = path.indexOf(segment);
    if (at >= 0) return path.slice(0, at + 1);
  }
  return path.endsWith('/') ? path : `${path.slice(0, path.lastIndexOf('/') + 1)}`;
}

export const APP_SCOPE = readScope();

/**
 * Every route is **one path segment deep**, and that is not cosmetic.
 *
 * The build emits relative asset URLs (`./assets/index-<hash>.js`), which the browser
 * resolves against the document's own directory. At `/p/<id>/library` that becomes
 * `/p/<id>/assets/…`, the SPA fallback answers it with HTML, and a cold-loaded deep link
 * fails before a line of PadLab runs. Keeping every path a single segment means `./assets`
 * always resolves to the app root, so a shared link works on any static host without
 * needing an absolute `base` configured at deploy time.
 */
function pathFor(route: Route): string {
  switch (route.name) {
    case 'home':
      return APP_SCOPE;
    case 'pack':
      return `${APP_SCOPE}pack-${route.packID ?? ''}`;
    case 'onboarding':
      return `${APP_SCOPE}welcome`;
    case 'board':
      return route.projectID === undefined ? `${APP_SCOPE}board` : `${APP_SCOPE}p-${route.projectID}`;
    case 'wave':
      return route.assetID === undefined ? `${APP_SCOPE}edit` : `${APP_SCOPE}edit-${route.assetID}`;
    default:
      return `${APP_SCOPE}${route.name}`;
  }
}

/** Turns a location into a route. Unknown paths land on Home rather than on nothing. */
export function parsePath(pathname: string): Route {
  const relative = pathname.startsWith(APP_SCOPE) ? pathname.slice(APP_SCOPE.length) : pathname.replace(/^\//, '');
  const segments = relative.split('/').filter((segment) => segment !== '' && segment !== 'index.html');
  const first = segments[0];
  if (first === undefined) return { name: 'home' };

  if (first.startsWith('p-')) return { name: 'board', projectID: first.slice(2) };
  if (first.startsWith('pack-')) return { name: 'pack', packID: first.slice(5) };
  if (first.startsWith('edit-')) return { name: 'wave', assetID: first.slice(5) };
  if (first === 'edit') return { name: 'wave' };
  if (first === 'welcome') return { name: 'onboarding' };
  // Legacy nested form, still parsed so a link shared before this build keeps working.
  if (first === 'p' && segments[1] !== undefined) {
    const projectID = segments[1];
    const leaf = segments[2];
    if (leaf === undefined || !isRouteName(leaf)) return { name: 'board', projectID };
    return { name: leaf, projectID };
  }
  if (isRouteName(first)) return { name: first };
  return { name: 'home' };
}

function isRouteName(value: string): value is RouteName {
  return Object.prototype.hasOwnProperty.call(ROUTES, value);
}

// MARK: - Live state

const routeSignal = signal<Route>(parsePath(location.pathname));
const depthSignal = signal(0);

export const route: ReadonlySignal<Route> = routeSignal;
export const definition: ReadonlySignal<RouteDefinition> = signal(ROUTES[routeSignal.peek().name]) as ReadonlySignal<RouteDefinition>;

/**
 * True when going back stays inside PadLab. In a browser tab the chrome offers its own back
 * button, but an installed app has none, so this drives whether a screen shows a chevron or
 * a close affordance.
 */
export const canGoBack: ReadonlySignal<boolean> = signal(false);

const depthMutable = depthSignal;
const definitionMutable = definition as unknown as { value: RouteDefinition };
const canGoBackMutable = canGoBack as unknown as { value: boolean };

/** The launch route, captured before anything navigates. Used to honour a deep link. */
export const launchRoute: Route = routeSignal.peek();

function commit(next: Route): void {
  routeSignal.value = next;
  definitionMutable.value = ROUTES[next.name];
  canGoBackMutable.value = depthSignal.peek() > 0;
}

// MARK: - View transitions
//
// A navigation is a state change first and an animation second. Everything in this section
// exists so the animation can fail without taking the state change down with it.

/**
 * The object `document.startViewTransition` returns, described structurally.
 *
 * No shipping Safari has the API, so the DOM lib's declaration is not evidence that the
 * runtime provides it; reading the property through `unknown` keeps the feature check a real
 * runtime test rather than one the compiler has already decided is unnecessary.
 */
interface ViewTransitionHandle {
  readonly finished: Promise<void>;
  readonly ready: Promise<void>;
  readonly updateCallbackDone: Promise<void>;
}

type TransitionStarter = (update: () => void) => ViewTransitionHandle;

/**
 * The transition currently animating, if any.
 *
 * Calling `startViewTransition` while one is in flight is what raises
 * `InvalidStateError: Transition was aborted because of invalid state`, and a double-tap on
 * the tab rail is fast enough to do it.
 */
let activeTransition: ViewTransitionHandle | null = null;

/**
 * Increments once per navigation so a transition's update callback — which the browser runs a
 * frame later, after it has captured the outgoing screen — can tell whether it is still the
 * newest one. A stale callback that applied anyway would send the user back to the screen
 * they just left.
 */
let navigationSeq = 0;

function transitionStarter(): TransitionStarter | null {
  const candidate = (document as unknown as Record<string, unknown>)['startViewTransition'];
  return typeof candidate === 'function'
    ? ((candidate as TransitionStarter).bind(document) as TransitionStarter)
    : null;
}

/**
 * True for the two failures that mean "this animation could not run", and nothing else. A
 * `TypeError` raised inside the update callback is a real defect and has to keep propagating.
 */
function isTransitionAborted(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === 'AbortError' || name === 'InvalidStateError';
}

/**
 * Applies `update`, animated where the browser can animate it and instantly where it cannot.
 *
 * The DOM change is never conditional on the animation succeeding: a transition the browser
 * refuses to start, or skips halfway through, still leaves the app on the screen the user
 * asked for. Reduce Motion takes the instant path — a cross-fade between two screens is
 * exactly the movement that setting exists to remove.
 */
async function applyWithTransition(update: () => void): Promise<void> {
  const seq = ++navigationSeq;
  const applyIfCurrent = (): void => {
    if (seq !== navigationSeq) return;
    update();
  };

  const start = transitionStarter();
  if (start === null || activeTransition !== null || prefersReducedMotion()) {
    applyIfCurrent();
    return;
  }

  let transition: ViewTransitionHandle;
  try {
    transition = start(applyIfCurrent);
  } catch (error) {
    if (!isTransitionAborted(error)) throw error;
    // The document was not in a state that permits a transition — hidden, or being unloaded.
    applyIfCurrent();
    return;
  }

  activeTransition = transition;
  // `ready` and `updateCallbackDone` are created by the browser whether or not anything reads
  // them, and both reject when a transition is skipped. Nothing here awaits them, so without a
  // sink every skipped transition surfaces as an unhandled rejection — which in a production
  // build, where log.ts has compiled out, is the only trace a bug report would ever carry.
  void transition.ready.catch(() => undefined);
  void transition.updateCallbackDone.catch(() => undefined);

  try {
    await transition.finished;
  } catch (error) {
    if (!isTransitionAborted(error)) throw error;
  } finally {
    if (activeTransition === transition) activeTransition = null;
  }
}

export interface NavigateOptions {
  /** Replace the current entry instead of pushing — used for redirects and tab switches. */
  replace?: boolean;
  /** Skip the view transition, for navigation that happens during a gesture. */
  instant?: boolean;
}

/**
 * The route the app is moving to while a transition captures the outgoing screen.
 *
 * The duplicate check below has to run against intent, not against what has been committed:
 * during a transition `routeSignal` still holds the *previous* screen, so comparing with it
 * would discard the second tap of "Beats, then Board" as a repeat of a screen the user had
 * already left.
 */
let pendingRoute: Route | null = null;

export async function navigate(next: Route, options: NavigateOptions = {}): Promise<void> {
  const current = pendingRoute ?? routeSignal.peek();
  const path = pathFor(next);
  if (path === pathFor(current) && next.name === current.name) return;
  pendingRoute = next;

  const apply = (): void => {
    try {
      if (options.replace === true) {
        history.replaceState({ depth: depthSignal.peek() }, '', path);
      } else {
        depthMutable.value = depthSignal.peek() + 1;
        history.pushState({ depth: depthSignal.peek() }, '', path);
      }
    } catch (error) {
      // A `file:` origin or a sandboxed frame refuses pushState. The app still navigates;
      // only the address stops updating, which is invisible in standalone mode anyway.
      log.warn('History update refused', { standalone: isStandalone.peek(), reason: tag(error instanceof Error ? error.name : 'unknown') });
    }
    commit(next);
    if (pendingRoute === next) pendingRoute = null;
  };

  if (options.instant === true) {
    navigationSeq++;
    apply();
    return;
  }
  await applyWithTransition(apply);
}

/** Goes back inside the app, or to the screen's declared fallback when there is no history. */
export function goBack(): void {
  if (depthSignal.peek() > 0) {
    history.back();
    return;
  }
  const fallback = ROUTES[routeSignal.peek().name].fallbackBack;
  const projectID = routeSignal.peek().projectID;
  void navigate(fallback === undefined ? { name: 'home' } : { name: fallback, ...(projectID === undefined ? {} : { projectID }) }, {
    replace: true,
  });
}

/** Switches tab without growing history — a tab rail that stacks entries is a trap. */
export function selectTab(name: RouteName, projectID?: string): void {
  void navigate({ name, ...(projectID === undefined ? {} : { projectID }) }, { replace: true });
}

export function start(): () => void {
  const onPop = (event: PopStateEvent): void => {
    const state = event.state as { depth?: number } | null;
    depthMutable.value = Math.max(0, state?.depth ?? 0);
    // The address bar has already moved, so it — not any in-flight intent — is the truth.
    pendingRoute = null;
    void applyWithTransition(() => commit(parsePath(location.pathname)));
  };
  window.addEventListener('popstate', onPop);
  history.replaceState({ depth: 0 }, '', location.pathname + location.search);
  commit(routeSignal.peek());
  log.info('Router ready', { scope: tag(APP_SCOPE), route: tag(routeSignal.peek().name) });
  return () => window.removeEventListener('popstate', onPop);
}

/** Absolute URL for a route, for Web Share and the QR code on the install screen. */
export function urlFor(next: Route): string {
  return new URL(pathFor(next), location.origin).toString();
}

export function describeRoute(next: Route): string {
  const base = ROUTES[next.name].title;
  return next.projectID === undefined ? base : `${base} · ${idPrefix(next.projectID)}`;
}
